import { useReducer, useContext } from "react"
import { useSession } from "source/shared/hooks/useSession"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import { useQRCode } from "source/shared/QRCode"
import { AmountInput } from "./components/AmountInput"
import { ContinueButton } from "./components/ContinueButton"
import { FormSubmit } from "./components/FormSubmit"
import { LeftPane } from "./components/LeftPane"
import { PaymentSecurityDisclaimer } from "./components/PaymentSecurityDisclaimer"
import { RightPane } from "./components/RightPane"
import { ConditionalStripeElementsProvider } from "./components/stripe"
import { FormFooter } from "./components/display/FormFooter"
import { FormWrapper } from "./components/display/FormWrapper"
import { PaneGroup } from "./components/display/PaneGroup"
import { DonationFormContext } from "./hooks/useDonationFormContext"
import { useMediaQuery } from "source/giving/hooks/useMediaQuery"
import { LocalStorageStatePersistence } from "./stores/local_storage"
import { reducer } from "./stores/reducer"
import { initializeState } from "./stores/initializeState"
import "./styles/donation-form-doxy-overrides.css"
import { useCalculatedTotalAndDesignations } from "./hooks/useCalculatedTotalAndDesignations"
import { useParsedConfig } from "./hooks/useParsedConfig"
import { useValidateProgress } from "./stores/validations"
import { DonationStatus } from "./components/DonationStatus"
import { BrowserNavigationHandler } from "./components/BrowserNavigationHandler"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"

export function DonationForm() {
  const config = useParsedConfig()
  const [state, dispatch] = useReducer(reducer, config, initializeState)
  const desktop = useMediaQuery(`(min-width: ${BREAKPOINTS.MD}px)`)
  const {
    meta: { authenticated },
  } = useSession(false)
  const {
    current_organization: { currency },
  } = useContext(ServerRenderedProps)

  const {
    focusedPane,
    focusedDesignationIndex,
    designations,
    frequency,
    editFrequencyExpanded,
    errors,
    feeCovered,
    formReady,
    stripePaymentMethod,
    savePaymentMethodChecked,
    pollingDonationStatusId,
  } = state

  const { totalCents, calculatedDesignations } =
    useCalculatedTotalAndDesignations(config, state)

  useQRCode()

  const validateProgress = useValidateProgress(state, config, dispatch)

  const twoColumn = desktop && authenticated
  const showAmount = desktop || focusedPane === "left"
  const showLeftPane = twoColumn || focusedPane === "left"
  const showRightPane = twoColumn || focusedPane === "right"
  const showContinue = !twoColumn && focusedPane === "left"
  const showSubmit = twoColumn || (!twoColumn && focusedPane === "right")

  return (
    <DonationFormContext.Provider value={{ config, dispatch }}>
      <ConditionalStripeElementsProvider
        condition={authenticated}
        {...{
          amountCents: totalCents,
          currency: currency.toLowerCase(),
          frequency,
          stripePaymentMethod,
          savePaymentMethodChecked,
        }}
      >
        {!desktop && (
          <BrowserNavigationHandler {...{ dispatch, focusedPane }} />
        )}
        <FormWrapper authenticated={authenticated}>
          {showAmount && <AmountInput {...{ designations, errors }} />}
          <PaneGroup>
            {showLeftPane && (
              <LeftPane
                {...{
                  designations,
                  frequency,
                  editFrequencyExpanded,
                  errors,
                  focusedDesignationIndex,
                }}
              />
            )}
            {showRightPane && (
              <RightPane
                {...{
                  designations,
                  desktop,
                  totalCents,
                  feeCovered,
                  frequency,
                  stripePaymentMethod,
                  savePaymentMethodChecked,
                }}
              />
            )}
          </PaneGroup>
          <FormFooter>
            {showContinue && (
              <>
                {!authenticated && (
                  <LocalStorageStatePersistence {...{ state }} />
                )}
                <ContinueButton {...{ authenticated, validateProgress }} />
              </>
            )}
            {pollingDonationStatusId && (
              <DonationStatus id={pollingDonationStatusId} />
            )}
            {showSubmit && (
              <FormSubmit
                {...{
                  state,
                  totalCents,
                  calculatedDesignations,
                  validateProgress,
                  disabled: !formReady,
                }}
              />
            )}
            <PaymentSecurityDisclaimer />
          </FormFooter>
        </FormWrapper>
      </ConditionalStripeElementsProvider>
    </DonationFormContext.Provider>
  )
}
